import { PaginatedResponse, PaginatedResponseData } from "@/types/types";
import { User } from "@/types/user";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createUser, deleteUser, updateUser } from "./api";

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: User) => createUser(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: User) => updateUser(values),

    onSuccess: (_response, values) => {
      // 1️⃣ obtener todas las queries relacionadas con "users"
      const queries = queryClient.getQueriesData<PaginatedResponse<User>>({
        queryKey: ["users"],
      });

      // 2️⃣ iterar cada cache (paginaciones, filtros, etc.)
      queries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        // 3️⃣ reemplazar el usuario si coincide el id
        const updatedItems = oldData.items.map((user) =>
          user.id === values.id ? { ...user, ...values } : user,
        );

        // 4️⃣ actualizar cache
        queryClient.setQueryData(queryKey, {
          ...oldData,
          items: updatedItems,
        });
      });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: User["id"]) => deleteUser(id),

    onSuccess: (_response, id) => {
      const queries = queryClient.getQueriesData<PaginatedResponse<User>>({
        queryKey: ["users"],
      });

      queries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        const updatedItems = oldData.items.filter((user) => user.id !== id);

        queryClient.setQueryData(queryKey, {
          ...oldData,
          items: updatedItems,
        });
      });
    },
  });
}
